// utils/adminSignInApi.ts
import { url as buildUrl } from "@/utils/apiBase";

// Base API configuration - kept for compatibility (concatenation with absolute URLs is a no-op)
const API_BASE_URL = ""; // absolute URLs from apiBase make this effectively ignored
const API_PREFIX = "/api/v1"; // retained but not used for endpoint construction below

// API endpoints resolved via apiBase (absolute admin origin, versioned)
const ENDPOINTS = {
  REQUEST_LOGIN_CODE: buildUrl("admin", "/admin/auth/request-code", "v1"),
  VERIFY_LOGIN_CODE:  buildUrl("admin", "/admin/auth/verify-code", "v1"),
  REFRESH_TOKEN:      buildUrl("admin", "/admin/auth/refresh", "v1"),
  LOGOUT:             buildUrl("admin", "/admin/auth/logout", "v1"),
  GET_ME:             buildUrl("admin", "/admin/auth/me", "v1"),
  AUTH_STATUS:        buildUrl("admin", "/admin/auth/status", "v1"),
  HEALTH_CHECK:       buildUrl("admin", "/admin/auth/health", "v1"),
} as const;

// Updated types to match your backend responses
export interface AdminLoginDto {
  email: string;
  password: string;
}

export interface AdminVerifyCodeDto {
  email: string;
  code: string;
}

export interface AdminLoginResponse {
  message: string;
}

export interface AdminVerifyResponse {
  success: boolean;
  message: string;
  admin: {
    id: string;
    email: string;
    username: string;
    role: string;
  };
}

export interface AdminMeResponse {
  success: boolean;
  admin: {
    id: string;
    email: string;
    username: string;
    role: string;
    isActive: boolean;
    lastLoginAt: Date;
    createdAt: Date;
  };
}

// Updated to match your backend /status endpoint response
export interface AdminAuthStatusResponse {
  authenticated: boolean;
  admin: {
    id: string;
    email: string;
    role: string;
  };
  timestamp: string;
}

export interface AdminLogoutResponse {
  success: boolean;
  message: string;
}

export interface AdminRefreshResponse {
  success: boolean;
  message: string;
  admin: {
    id: string;
    email: string;
    username: string;
    role: string;
  };
}

export interface AdminHealthResponse {
  status: string;
  service: string;
  timestamp: string;
  uptime: number;
}

export interface ApiError {
  message: string;
  statusCode?: number;
  error?: string;
}

// Utility function to handle API responses
async function handleApiResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  
  if (!contentType?.includes('application/json')) {
    throw new Error('Invalid response format from server');
  }

  const data = await response.json();

  if (!response.ok) {
    const error: ApiError = {
      message: data.message || 'An error occurred',
      statusCode: response.status,
      error: data.error,
    };
    throw error;
  }

  return data;
}

// Updated utility function to make API requests with better error handling
async function makeApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`; // endpoint is already absolute via apiBase
  
  const config: RequestInit = {
    credentials: 'include', // Important: Include cookies in requests
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  console.log(`🔗 Making API request to: ${url}`); // Debug log

  try {
    const response = await fetch(url, config);
    return await handleApiResponse<T>(response);
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check your connection.');
    }
    
    // Re-throw API errors
    throw error;
  }
}

// API functions updated to match your backend
export async function requestAdminLoginCode(
  email: string,
  password: string
): Promise<AdminLoginResponse> {
  const loginData: AdminLoginDto = {
    email: email.trim().toLowerCase(),
    password,
  };

  return makeApiRequest<AdminLoginResponse>(ENDPOINTS.REQUEST_LOGIN_CODE, {
    method: 'POST',
    body: JSON.stringify(loginData),
  });
}

export async function verifyAdminLoginCode(
  email: string,
  code: string
): Promise<AdminVerifyResponse> {
  const verifyData: AdminVerifyCodeDto = {
    email: email.trim().toLowerCase(),
    code: code.trim().toUpperCase(),
  };

  return makeApiRequest<AdminVerifyResponse>(ENDPOINTS.VERIFY_LOGIN_CODE, {
    method: 'POST',
    body: JSON.stringify(verifyData),
  });
}

export async function refreshAdminToken(): Promise<AdminRefreshResponse> {
  return makeApiRequest<AdminRefreshResponse>(ENDPOINTS.REFRESH_TOKEN, {
    method: 'POST',
  });
}

export async function logoutAdmin(): Promise<AdminLogoutResponse> {
  return makeApiRequest<AdminLogoutResponse>(ENDPOINTS.LOGOUT, {
    method: 'POST',
  });
}

export async function getCurrentAdmin(): Promise<AdminMeResponse> {
  return makeApiRequest<AdminMeResponse>(ENDPOINTS.GET_ME, {
    method: 'GET',
  });
}

// Updated to match your backend /status endpoint
export async function checkAuthStatus(): Promise<AdminAuthStatusResponse> {
  return makeApiRequest<AdminAuthStatusResponse>(ENDPOINTS.AUTH_STATUS, {
    method: 'GET',
  });
}

// New health check function
export async function checkApiHealth(): Promise<AdminHealthResponse> {
  return makeApiRequest<AdminHealthResponse>(ENDPOINTS.HEALTH_CHECK, {
    method: 'GET',
  });
}

// Updated AdminSignInFlow class
export class AdminSignInFlow {
  private email: string;
  private password: string;

  constructor(email: string, password: string) {
    this.email = email.trim().toLowerCase();
    this.password = password;
  }

  async requestCode(): Promise<AdminLoginResponse> {
    return requestAdminLoginCode(this.email, this.password);
  }

  async verifyCode(code: string): Promise<AdminVerifyResponse> {
    return verifyAdminLoginCode(this.email, code);
  }

  getEmail(): string {
    return this.email;
  }
}

// Updated AdminAuthManager class to match backend responses
export class AdminAuthManager {
  // Check if user is authenticated using your backend
  static async isAuthenticated(): Promise<boolean> {
    try {
      const status = await checkAuthStatus();
      return status.authenticated; // Updated to match backend property name
    } catch (error) {
      console.error('Auth status check failed:', error);
      return false;
    }
  }

  // Get admin data from your backend
  static async getAdminData(): Promise<AdminMeResponse['admin'] | null> {
    try {
      const response = await getCurrentAdmin();
      return response.admin;
    } catch (error) {
      console.error('Get admin data failed:', error);
      return null;
    }
  }

  // Logout using your backend
  static async logout(): Promise<void> {
    try {
      await logoutAdmin();
    } catch (error) {
      console.warn('Logout API call failed, but continuing with local cleanup');
    }
  }

  // Refresh tokens using your backend
  static async refreshAuth(): Promise<AdminRefreshResponse | null> {
    try {
      return await refreshAdminToken();
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }

  // Check if user has valid authentication (simplified)
  static async hasValidTokens(): Promise<boolean> {
    try {
      const status = await checkAuthStatus();
      return status.authenticated;
    } catch (error) {
      return false;
    }
  }

  // Get current user info from auth status (lighter weight than /me)
  static async getCurrentUserInfo(): Promise<AdminAuthStatusResponse['admin'] | null> {
    try {
      const status = await checkAuthStatus();
      return status.authenticated ? status.admin : null;
    } catch (error) {
      return null;
    }
  }

  // Check API health
  static async checkHealth(): Promise<boolean> {
    try {
      const health = await checkApiHealth();
      return health.status === 'ok';
    } catch (error) {
      return false;
    }
  }
}

// Error handling utilities
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return (error as ApiError).message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected error occurred';
}

export function isApiError(error: unknown): error is ApiError {
  return error !== null && 
         typeof error === 'object' && 
         'message' in error;
}

export function isCredentialsError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.statusCode === 401 || 
           error.message.toLowerCase().includes('invalid credentials') ||
           error.message.toLowerCase().includes('unauthorized');
  }
  return false;
}

export function isAccountLockedError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.message.toLowerCase().includes('locked') ||
           error.message.toLowerCase().includes('blocked') ||
           error.message.toLowerCase().includes('temporarily');
  }
  return false;
}

export function isTokenExpiredError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.statusCode === 401 && (
      error.message.toLowerCase().includes('expired') ||
      error.message.toLowerCase().includes('invalid token') ||
      error.message.toLowerCase().includes('refresh')
    );
  }
  return false;
}

export function isAuthenticationError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.statusCode === 401;
  }
  return false;
}